/**
 * Symptom Assessment Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Slider } from "../ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Activity, Save, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import SpeechDictation from "../SpeechDictation";

export function SymptomAssessmentOverlay({ patientId, patientName, onSave, onClose }) {
  const [formData, setFormData] = useState({
    symptoms: [],
    overall_comfort: 5,
    interventions: [],
    notes: "",
  });

  const symptomCategories = [
    { id: "pain", label: "Pain", color: "red" },
    { id: "dyspnea", label: "Dyspnea/Shortness of Breath", color: "blue" },
    { id: "nausea", label: "Nausea/Vomiting", color: "yellow" },
    { id: "constipation", label: "Constipation", color: "orange" },
    { id: "anxiety", label: "Anxiety", color: "purple" },
    { id: "depression", label: "Depression/Sadness", color: "indigo" },
    { id: "fatigue", label: "Fatigue/Weakness", color: "gray" },
    { id: "anorexia", label: "Anorexia/Poor Appetite", color: "amber" },
    { id: "insomnia", label: "Insomnia/Sleep Issues", color: "slate" },
    { id: "confusion", label: "Confusion/Delirium", color: "pink" },
    { id: "edema", label: "Edema/Swelling", color: "cyan" },
    { id: "skin", label: "Skin Issues", color: "lime" },
  ];

  const [symptomDetails, setSymptomDetails] = useState({});

  const toggleSymptom = (symptomId) => {
    if (formData.symptoms.includes(symptomId)) {
      setFormData({
        ...formData,
        symptoms: formData.symptoms.filter(s => s !== symptomId)
      });
      const newDetails = { ...symptomDetails };
      delete newDetails[symptomId];
      setSymptomDetails(newDetails);
    } else {
      setFormData({
        ...formData,
        symptoms: [...formData.symptoms, symptomId]
      });
      setSymptomDetails({
        ...symptomDetails,
        [symptomId]: { severity: 5, frequency: "intermittent", trend: "stable" }
      });
    }
  };

  const updateSymptomDetail = (symptomId, field, value) => {
    setSymptomDetails({
      ...symptomDetails,
      [symptomId]: { ...symptomDetails[symptomId], [field]: value }
    });
  };

  const handleSave = () => {
    const data = {
      ...formData,
      symptom_details: symptomDetails
    };
    toast.success("Symptom assessment saved");
    if (onSave) onSave(data);
    if (onClose) onClose();
  };

  return (
    <div className="space-y-4 p-4" data-testid="symptom-assessment-overlay">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="w-5 h-5 text-green-400" />
        <h3 className="font-semibold text-white">Symptom Assessment</h3>
        <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Select Active Symptoms</Label>
        <div className="flex flex-wrap gap-1.5">
          {symptomCategories.map((symptom) => (
            <Badge 
              key={symptom.id}
              variant={formData.symptoms.includes(symptom.id) ? "default" : "outline"}
              className={`cursor-pointer transition-colors ${
                formData.symptoms.includes(symptom.id) 
                  ? "bg-teal-600 text-white" 
                  : "border-slate-600 text-slate-300 hover:bg-slate-700"
              }`}
              onClick={() => toggleSymptom(symptom.id)}
            >
              {symptom.label}
            </Badge>
          ))}
        </div>
      </div>

      {formData.symptoms.length > 0 && (
        <div className="space-y-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
          <Label className="text-slate-200 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Symptom Details
          </Label>
          
          {formData.symptoms.map((symptomId) => {
            const symptom = symptomCategories.find(s => s.id === symptomId);
            const detail = symptomDetails[symptomId] || { severity: 5, frequency: "intermittent", trend: "stable" };
            
            return (
              <div key={symptomId} className="space-y-2 p-3 bg-slate-900/50 rounded-lg">
                <p className="font-medium text-slate-200">{symptom?.label}</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">Severity (1-10)</Label>
                    <div className="flex items-center gap-2">
                      <Slider 
                        value={[detail.severity]} 
                        onValueChange={([v]) => updateSymptomDetail(symptomId, "severity", v)} 
                        max={10} 
                        step={1}
                        className="flex-1"
                      />
                      <Badge className={`${detail.severity <= 3 ? "bg-green-500" : detail.severity <= 6 ? "bg-yellow-500" : "bg-red-500"} text-white`}>
                        {detail.severity}
                      </Badge>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">Frequency</Label>
                    <Select value={detail.frequency} onValueChange={(v) => updateSymptomDetail(symptomId, "frequency", v)}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="constant">Constant</SelectItem>
                        <SelectItem value="frequent">Frequent</SelectItem>
                        <SelectItem value="intermittent">Intermittent</SelectItem>
                        <SelectItem value="occasional">Occasional</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400">Trend</Label>
                    <Select value={detail.trend} onValueChange={(v) => updateSymptomDetail(symptomId, "trend", v)}>
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="worsening">Worsening</SelectItem>
                        <SelectItem value="stable">Stable</SelectItem>
                        <SelectItem value="improving">Improving</SelectItem>
                        <SelectItem value="new">New Onset</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-2">
        <Label className="text-slate-200">Overall Comfort Level (1-10)</Label>
        <div className="flex items-center gap-4">
          <Slider 
            value={[formData.overall_comfort]} 
            onValueChange={([v]) => setFormData({...formData, overall_comfort: v})} 
            max={10} 
            step={1}
            className="flex-1"
          />
          <Badge className={`${formData.overall_comfort >= 7 ? "bg-green-500" : formData.overall_comfort >= 4 ? "bg-yellow-500" : "bg-red-500"} text-white`}>
            {formData.overall_comfort}/10
          </Badge>
        </div>
        <p className="text-xs text-slate-400">10 = Very Comfortable, 1 = Very Uncomfortable</p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-slate-200">Assessment Notes</Label>
          <SpeechDictation onTranscript={(t) => setFormData({...formData, notes: formData.notes + " " + t})} buttonSize="sm" />
        </div>
        <Textarea 
          value={formData.notes} 
          onChange={(e) => setFormData({...formData, notes: e.target.value})} 
          placeholder="Additional observations, interventions, and recommendations..." 
          rows={3} 
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
        <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700"><Save className="w-4 h-4 mr-1" />Save Assessment</Button>
      </div>
    </div>
  );
}

export default SymptomAssessmentOverlay;
